import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import {
  AppConfig,
  LAYOUT_CONFIG_DEFAULTS,
  LAYOUT_CONFIG_STORAGE_KEY,
  mergeLayoutConfigWithDefaults,
  writeStoredLayoutConfig,
} from '../layout/service/layout-config-storage';
import { COMPLETED_VALUE, TUTORIAL_STORAGE_PREFIX } from '../caas/tutorial/tutorial.service';

// ── Shared interface (mirrors the backend UserPreferences type) ───────────────

/**
 * Combined preferences object persisted to DynamoDB.
 *
 * - `layout` mirrors the `AppConfig` shape from layout-config-storage.ts.
 * - `tutorialCompletions` is a map of `toolId → true` for every tour the user
 *   has completed or skipped.
 *
 * Both fields also continue to be written individually to localStorage as the
 * fast, synchronous read path.  This backend record is the source of truth for
 * cross-device sync only.
 */
export interface UserPreferences {
  layout: AppConfig;
  tutorialCompletions: Record<string, boolean>;
}

// ── localStorage key for tutorial completions ────────────────────────────────

/**
 * Reads all tutorial completion flags from localStorage.
 * Keys match the pattern `centcom:tutorial:{username}:{toolId}` and a value
 * of `'done'` means completed.  We collect them into a plain map of
 * `toolId → true` (dropping the username prefix since the backend is
 * already scoped to one user).
 */
function readTutorialCompletions(username: string): Record<string, boolean> {
  const prefix = `${TUTORIAL_STORAGE_PREFIX}:${username}:`;
  const completions: Record<string, boolean> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && localStorage.getItem(key) === COMPLETED_VALUE) {
        const toolId = key.slice(prefix.length);
        completions[toolId] = true;
      }
    }
  } catch {
    // localStorage unavailable — return empty
  }
  return completions;
}

/**
 * Writes tutorial completion flags back into localStorage so that
 * `TutorialService.hasCompleted()` continues to work without any changes.
 */
function writeTutorialCompletions(
  username: string,
  completions: Record<string, boolean>,
): void {
  try {
    for (const [toolId, done] of Object.entries(completions)) {
      if (done) {
        localStorage.setItem(`${TUTORIAL_STORAGE_PREFIX}:${username}:${toolId}`, COMPLETED_VALUE);
      }
    }
  } catch {
    // fail silently
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly endpoint = '/api/preferences';

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService,
  ) {}

  // ── Username helper ─────────────────────────────────────────────────────────

  private getUsername(): string {
    try {
      return (
        this.msalService.instance.getActiveAccount()?.username ?? 'anonymous'
      );
    } catch {
      return 'anonymous';
    }
  }

  // ── Build / apply ───────────────────────────────────────────────────────────

  /**
   * Reads both localStorage sources and assembles the current `UserPreferences`
   * snapshot to be sent to the backend.
   */
  buildCurrentPreferences(): UserPreferences {
    const username = this.getUsername();

    // Layout config — read directly from localStorage, fall back to defaults.
    let layout: AppConfig;
    try {
      const raw = localStorage.getItem(LAYOUT_CONFIG_STORAGE_KEY);
      const stored = raw ? (JSON.parse(raw) as Partial<AppConfig>) : null;
      layout = mergeLayoutConfigWithDefaults(LAYOUT_CONFIG_DEFAULTS, stored);
    } catch {
      layout = { ...LAYOUT_CONFIG_DEFAULTS };
    }

    return {
      layout,
      tutorialCompletions: readTutorialCompletions(username),
    };
  }

  /**
   * Applies a `UserPreferences` record received from the backend into
   * localStorage.  This is called once on login so that the rest of the app
   * (LayoutService, TutorialService) continues to read from localStorage
   * without any changes.
   */
  applyPreferences(prefs: UserPreferences): void {
    const username = this.getUsername();

    // Apply layout config to localStorage.
    try {
      writeStoredLayoutConfig(prefs.layout);
    } catch {
      // fail silently
    }

    // Apply tutorial completions to localStorage.
    writeTutorialCompletions(username, prefs.tutorialCompletions ?? {});
  }

  // ── Backend sync ────────────────────────────────────────────────────────────

  /**
   * Fetches the persisted preferences from the backend and applies them to
   * localStorage.  Call this once after the user authenticates so that
   * cross-device changes are reflected.
   *
   * If the server returns an empty record (first-time user) the local state
   * is left unchanged.
   */
  loadFromBackend(): Observable<UserPreferences | Record<string, never>> {
    return this.http
      .get<UserPreferences | Record<string, never>>(this.endpoint)
      .pipe(
        tap((prefs) => {
          // Only apply if the response looks like a real preferences record.
          if (prefs && 'layout' in prefs) {
            this.applyPreferences(prefs as UserPreferences);
          }
        }),
        catchError((err) => {
          console.warn('[UserPreferencesService] Failed to load preferences from backend:', err);
          return of({});
        }),
      );
  }

  /**
   * Persists the current local preferences state to the backend.
   * Fire-and-forget — errors are logged but not surfaced to the caller.
   * Call this any time a preference changes (layout config or tutorial completion).
   */
  saveToBackend(): void {
    const prefs = this.buildCurrentPreferences();
    this.http.put<{ ok: boolean }>(this.endpoint, prefs).subscribe({
      error: (err) =>
        console.warn('[UserPreferencesService] Failed to save preferences to backend:', err),
    });
  }
}
